// 트레일(리본): 움직이는 끝점이 지나간 자리를 꼬리처럼 잇는 얇은 띠. 끝점의 경로는 수식이라(직선 또는 호) 지난 위치를 따로 저장하지 않는다.
// 쓰는 칸: variant 0 = 직선(length m를 날아간다) / 1 = 호(length = 반경, start·sweep·roll·ahead는 아크와 같은 평면) /
//          2 = 직선 + 카메라를 향한 폭(c013 돌진 속도선: roll 대신 선 방향 × 시선 방향으로 폭을 펼쳐 어느 방향으로 달려도 납작하게 보이지 않는다),
//          width = 머리 쪽 두께(m), tail = 꼬리 길이(초), life = 끝점이 움직이는 시간 + 꼬리가 따라잡는 시간. 드로우콜 1.
import * as THREE from 'three/webgpu';
import { abs, clamp, mix, smoothstep, texture, uv, vec2 } from 'three/tsl';
import { BatchModule, fxAttributes, type Slot } from './batch';
import { stripLayout } from './layout';
import { clamp01, easeOutQuad, planeFrame } from './path';
import { MODULE_CAPS, type ModuleKind } from './types';

export const TRAIL_SEG = 16;
/** 기본 시선 방향(전투 카메라: 남쪽 위에서 북쪽 아래로 약 40°). 렌더가 setView로 매 프레임 바꾼다 */
const VIEW0 = [0, -0.64, -0.77] as const;

function makeMaterial(noise: THREE.Texture): THREE.MeshBasicNodeMaterial {
  const { ctl, t1, t2 } = fxAttributes();
  const age = ctl.x, env = ctl.y, seed = ctl.z;
  const u = uv().x, v = uv().y;
  const band = abs(v.mul(2).sub(1));
  const across = band.oneMinus().pow(1.2);
  // u: 꼬리(0) → 머리(1). 꼬리로 갈수록 투명
  const along = u.pow(1.5);
  const streak = texture(noise, vec2(u.mul(4).sub(age.mul(t2.w)), v.mul(0.6).add(seed))).g;
  const alpha = along.mul(across).mul(streak.mul(0.6).add(0.55)).mul(env);
  const hot = clamp(smoothstep(0.55, 1.0, u).mul(0.6).add(band.oneMinus().pow(4).mul(0.4)), 0, 1);
  const col = mix(t1.rgb, t2.rgb, hot).mul(t1.w).mul(0.7);

  const m = new THREE.MeshBasicNodeMaterial();
  m.transparent = true;
  m.depthWrite = false;
  m.side = THREE.DoubleSide;
  m.blending = THREE.AdditiveBlending;
  m.forceSinglePass = true;
  m.fog = false;
  m.colorNode = col;
  m.opacityNode = alpha;
  return m;
}

export class TrailModule extends BatchModule {
  readonly kind: ModuleKind = 'trail';

  /** 시선 방향(단위 벡터, variant 2가 폭을 펼치는 기준) */
  private vx: number = VIEW0[0]; private vy: number = VIEW0[1]; private vz: number = VIEW0[2];

  constructor(noise: THREE.Texture, cap = MODULE_CAPS.trail) {
    super(cap, stripLayout(TRAIL_SEG), makeMaterial(noise), 22, 'fx-trail');
  }

  /** 카메라 시선 방향(월드). 길이가 0이면 무시한다 */
  setView(x: number, y: number, z: number): void {
    const l = Math.hypot(x, y, z);
    if (l < 1e-6) return;
    this.vx = x / l; this.vy = y / l; this.vz = z / l;
  }

  protected positions(s: Slot, out: Float32Array, o: number): void {
    const f = planeFrame(s.yaw, s.roll);
    const tail = Math.max(0.02, s.tail);
    const T = Math.max(0.03, s.life - tail);
    const W = s.width * s.scale;
    const L = s.length * s.scale;
    const cx = s.x + f[0] * s.ahead, cy = s.y, cz = s.z + f[2] * s.ahead;
    const arc = s.variant >= 0.5 && s.variant < 1.5;
    const facing = s.variant >= 1.5;
    // variant 2: 폭 방향 = 선 방향(e1) × 시선. 선이 시선과 거의 나란하면 기본 평면 폭(e2)을 쓴다
    let wx = f[3], wy = f[4], wz = f[5];
    if (facing) {
      const cx2 = f[1] * this.vz - f[2] * this.vy, cy2 = f[2] * this.vx - f[0] * this.vz, cz2 = f[0] * this.vy - f[1] * this.vx;
      const cl = Math.hypot(cx2, cy2, cz2);
      if (cl > 0.15) { wx = cx2 / cl; wy = cy2 / cl; wz = cz2 / cl; }
    }
    for (let k = 0; k <= TRAIL_SEG; k++) {
      const frac = k / TRAIL_SEG;
      // 이 점이 끝점이던 시각: 머리(k = 끝) = 지금, 꼬리 쪽일수록 과거
      const tk = Math.min(T, Math.max(0, s.age - tail * (1 - frac)));
      const p = easeOutQuad(clamp01(tk / T));
      let px: number, py: number, pz: number, dx: number, dy: number, dz: number;
      if (arc) {
        const a = s.start + s.sweep * p;
        const ca = Math.cos(a), sa = Math.sin(a);
        dx = f[0] * ca + f[3] * sa; dy = f[1] * ca + f[4] * sa; dz = f[2] * ca + f[5] * sa;
        px = cx + dx * L; py = cy + dy * L; pz = cz + dz * L;
      } else {
        px = cx + f[0] * L * p; py = cy + f[1] * L * p; pz = cz + f[2] * L * p;
        dx = wx; dy = wy; dz = wz;
      }
      const hw = 0.5 * W * Math.pow(frac, 0.9);
      const q = o + k * 6;
      out[q] = px - dx * hw; out[q + 1] = py - dy * hw; out[q + 2] = pz - dz * hw;
      out[q + 3] = px + dx * hw; out[q + 4] = py + dy * hw; out[q + 5] = pz + dz * hw;
    }
  }

  protected ctl(s: Slot, out: Float32Array): void {
    const age01 = clamp01(s.age / s.life);
    out[0] = age01;
    out[1] = Math.min(1, s.age / 0.02) * (1 - Math.max(0, (age01 - 0.8) / 0.2));
    out[2] = s.seed;
    out[3] = 0;
  }

  protected override tint2w(): number { return 2.2; }
}
